"use client";

import { useEffect, useRef, useState } from "react";

const PAGES = ["/", "/pricing", "/blog/seo-checklist", "/blog/ai-content-quality", "/about", "/services/drain-cleaning", "/contact", "/blog/keyword-research-tips"];
const TOTAL = 40;
const SCAN_MS = 5000;
const RESULT_MS = 9000;

const FILES: { name: string; note: string }[] = [
  { name: "sitemap.xml", note: "Only pages that belong in it, with the reason for each one left out" },
  { name: "robots.txt", note: "Your current rules kept, with only safe lines added" },
  { name: "schema.json", note: "Structured data written from what your pages really say" },
  { name: "page-guidance.pdf", note: "What to fix on every page, critical first" },
];

/**
 * An example of a site scan, played in the page: five seconds of scanning,
 * then what the scan hands back. It starts when it scrolls into view and
 * repeats. With reduced motion switched on it shows the result straight away.
 */
export function ScanToFiles() {
  const ref = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<"idle" | "scanning" | "done">("idle");
  const [elapsed, setElapsed] = useState(0);
  const [run, setRun] = useState(0);

  // Start the first time it is on screen.
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setPhase(still ? "done" : "scanning");
          io.disconnect();
        }
      },
      { threshold: 0.35 },
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  // Scan for five seconds, show the result, then go round again.
  useEffect(() => {
    if (phase === "scanning") {
      const started = Date.now();
      const tick = setInterval(() => {
        const ms = Date.now() - started;
        if (ms >= SCAN_MS) {
          setElapsed(SCAN_MS);
          setPhase("done");
        } else setElapsed(ms);
      }, 120);
      return () => clearInterval(tick);
    }
    if (phase === "done" && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      const again = setTimeout(() => {
        setElapsed(0);
        setRun((n) => n + 1);
        setPhase("scanning");
      }, RESULT_MS);
      return () => clearTimeout(again);
    }
  }, [phase, run]);

  const progress = Math.min(elapsed / SCAN_MS, 1);
  const count = Math.round(progress * TOTAL);
  const page = PAGES[Math.floor(elapsed / 620) % PAGES.length];

  return (
    <div ref={ref} className="relative min-h-[348px]" aria-live="polite">
      {phase !== "done" ? (
        <div className="flex min-h-[348px] flex-col justify-center rounded-[22px] bg-white/[0.07] p-6 ring-1 ring-white/10">
          <div className="mb-5 flex items-center gap-3">
            <span className="relative flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-violet to-violet-deep">
              <span className="absolute inset-0 animate-ping rounded-2xl bg-violet/40" />
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="relative text-white" aria-hidden>
                <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="2.2" />
                <path d="M16 16l4.5 4.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
              </svg>
            </span>
            <div>
              <div className="text-[1.02rem] font-extrabold text-white">Scanning your site</div>
              <div className="text-[0.8rem] text-white/55">Reading the sitemap and following links</div>
            </div>
          </div>
          <div className="mb-3 flex items-center justify-between text-[0.86rem] text-white/75">
            <span className="truncate font-mono">yoursite.com{page}</span>
            <span className="tabular-nums flex-shrink-0 pl-3 font-bold">
              {count} / {TOTAL}
            </span>
          </div>
          <div className="shimmer-bar h-2.5 overflow-hidden rounded-full bg-white/10">
            <div className="h-full rounded-full bg-gradient-to-r from-violet via-violet-light to-pink transition-[width] duration-150 ease-linear" style={{ width: `${Math.round(progress * 100)}%` }} />
          </div>
          <dl className="mt-6 grid grid-cols-3 gap-3 text-center">
            {[
              { label: "Pages read", value: count },
              { label: "Issues found", value: Math.round(progress * 63) },
              { label: "Files prepared", value: Math.min(Math.floor(progress * 4.2), 4) },
            ].map((s) => (
              <div key={s.label} className="rounded-2xl bg-white/[0.06] px-2 py-3">
                <dd className="tabular-nums text-[1.5rem] font-extrabold leading-none text-white">{s.value}</dd>
                <dt className="mt-1.5 text-[0.72rem] font-semibold text-white/55">{s.label}</dt>
              </div>
            ))}
          </dl>
        </div>
      ) : (
        <div key={run} className="hero-enter">
          <div className="mb-3 flex items-center justify-between gap-3">
            <span className="inline-flex items-center gap-2 text-[0.9rem] font-extrabold text-white">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-violet to-violet-deep">
                <svg viewBox="0 0 24 24" fill="none" className="h-3.5 w-3.5 text-white" aria-hidden>
                  <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              Scan complete: {TOTAL} pages, 4 files ready
            </span>
            <button
              type="button"
              onClick={() => {
                setElapsed(0);
                setRun((n) => n + 1);
                setPhase("scanning");
              }}
              className="rounded-lg bg-white/10 px-3 py-1.5 text-[0.74rem] font-bold text-white/80 ring-1 ring-white/15 hover:bg-white/20 hover:text-white"
            >
              Play again
            </button>
          </div>
          <ul className="space-y-2.5">
            {FILES.map((f, i) => (
              <li key={f.name} className="hero-enter flex items-center gap-4 rounded-[18px] bg-white/[0.07] px-4 py-3 ring-1 ring-white/10" style={{ animationDelay: `${i * 0.09}s` }}>
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-violet to-violet-deep text-white">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                    <path d="M14 3v5h5" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                  </svg>
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-mono text-[0.9rem] font-bold text-white">{f.name}</span>
                  <span className="block text-[0.8rem] leading-snug text-white/55">{f.note}</span>
                </span>
                <span className="hidden rounded-lg bg-white/10 px-2.5 py-1 text-[0.7rem] font-extrabold text-violet-light ring-1 ring-white/15 sm:block">Ready</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="mt-3 text-[0.74rem] text-white/40">An example, played here. Your own scan reads your real pages.</p>
    </div>
  );
}
